import React from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';

const DocumentsTab = ({ register, watch, setValue, documents, setDocuments, documentTypes,errors }) => {
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    const newDocuments = files.map(file => ({
      id: Date.now() + Math.random(),
      file,
      doc_name: file.name,
      doc_type_id: null,
      doc_description: null,
      doc_number: null,
      doc_expiry_date: null,
      doc_notify_before_expiry: false,
      url: URL.createObjectURL(file) // Add URL for preview
    }));
    setDocuments([...documents, ...newDocuments]);
    // Set value for each new item without overriding the whole array
    newDocuments.forEach((doc, index) => {
      const docIndex = documents.length + index;
      setValue(`documents.${docIndex}`, doc); // Update only the new index
    });
  };

    
  const handleDocumentDelete = (docId) => {
    const updatedDocuments = documents.filter(doc => doc.id !== docId);
    setDocuments(updatedDocuments);
    setValue('documents', updatedDocuments);

    const existingRemovedIds = watch('document_removed_ids') || [];
    const updatedRemovedIds = [...new Set([...existingRemovedIds, docId])];
    setValue('document_removed_ids', updatedRemovedIds);
  };

  const handleDocumentChange = (docId, field, value) => {
    const updatedDocuments = documents.map((doc, index) => {
      if (doc.id === docId) {
        // Update form value for only this field
        setValue(`documents.${index}.${field}`, value);
        return { ...doc, [field]: value };
      }
      return doc;
    });
  
    setDocuments(updatedDocuments);
  };
  
  return (
    <div className="space-y-6">
      {/* Document Upload Section */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Upload Documents</label>
        <div className="mt-1">
          <input
            type="file"
            multiple
            onChange={handleFileUpload}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          <p className="mt-1 text-sm text-gray-500">Hold Ctrl to select multiple files</p>
        </div>
      </div>

      {/* Document List */}
      
      <div className="space-y-4">
        {documents.map((doc,index) => (
          <div key={doc.id} className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-start justify-between">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Document Preview */}
                <div className="md:col-span-2 flex items-center space-x-4 mb-4">
                  <div className="flex-shrink-0">
                    {doc.url ? (
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#009efb] hover:text-[#0089db] text-sm flex items-center"
                      >
                        <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        {doc.doc_name}
                      </a>
                    ) : (
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#009efb] hover:text-[#0089db] text-sm flex items-center"
                      >
                        <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        {doc.doc_file}
                      </a>
                    )}
                  </div>
                </div>

                {/* Document Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Document Type</label>
                  <select
                    {...register(`documents.${index}.doc_type_id`)}
                    value={doc.doc_type_id}
                    onChange={(e) => handleDocumentChange(doc.id, 'doc_type_id', e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
                  >
                    <option value="">Select Type</option>
                    {documentTypes?.map((type) => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                  {errors.doc_type_id && <p className="text-red-500 text-sm">{errors.doc_type_id.message}</p>}
                </div>

                {/* Document Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Document Name</label>
                  <input
                    {...register(`documents.${index}.doc_name`)}
                    type="text"
                    value={doc.doc_name}
                    onChange={(e) => handleDocumentChange(doc.id, 'doc_name', e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
                  />
                  {errors.doc_name && <p className="text-red-500 text-sm">{errors.doc_name.message}</p>}
                </div>

                {/* Document Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Document Number</label>
                  <input
                    {...register(`documents.${index}.doc_number`)}
                    type="text"
                    value={doc.doc_number}
                    onChange={(e) => handleDocumentChange(doc.id, 'doc_number', e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
                  />
                  {errors.doc_number && <p className="text-red-500 text-sm">{errors.doc_number.message}</p>}
                </div>

                {/* Expiration Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Expiration Date</label>
                  <input
                    {...register(`documents.${index}.doc_expiry_date`)}
                    type="text"
                    onInput={(e) => {
                      console.log(e.target.value);
                      setValue(`documents.${index}.doc_expiry_date`, e.target.value);
                    }}
                    value={doc.doc_expiry_date}
                    onChange={(e) => handleDocumentChange(doc.id, 'doc_expiry_date', e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm datepicker"
                  />
                  {errors.doc_expiry_date && <p className="text-red-500 text-sm">{errors.doc_expiry_date.message}</p>}
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    {...register(`documents.${index}.doc_description`)}
                    value={doc.doc_description}
                    onChange={(e) => handleDocumentChange(doc.id, 'doc_description', e.target.value)}
                    rows="2"
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
                  />
                  {errors.doc_description && <p className="text-red-500 text-sm">{errors.doc_description.message}</p>}
                </div>

                {/* Notify Before Expiry */}
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      {...register(`documents.${index}.doc_notify_before_expiry`)}
                      type="checkbox"
                      checked={doc.doc_notify_before_expiry}
                      onChange={(e) => handleDocumentChange(doc.id, 'doc_notify_before_expiry', e.target.checked)}
                      className="rounded border-gray-300 text-[#009efb] focus:ring-[#009efb]"
                    />
                    <span className="text-sm text-gray-700">Notify before expiry</span>
                  </label>
                  {errors.doc_notify_before_expiry && <p className="text-red-500 text-sm">{errors.doc_notify_before_expiry.message}</p>}
                </div>
              </div>

              {/* Delete Button */}
              <button
                type="button"
                onClick={() => handleDocumentDelete(doc.id)}
                className="ml-4 p-2 text-red-600 hover:text-red-800"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DocumentsTab; 